import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Search, Download, Eye, Trash2, ShoppingCart, Calendar, CheckSquare, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminLayout from '../../components/AdminLayout';
import AdminOrderEditor from './AdminOrderEditor';

const API_URL = window.location.origin;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWeek, setFilterWeek] = useState('all');
  const [deliveryWeeks, setDeliveryWeeks] = useState([]);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchOrders();
    fetchDeliveryWeeks();
    fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setClients(data.filter(u => u.role === 'client'));
    } catch {
      // Error handled silently
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/orders`, { withCredentials: true });
      setOrders(data);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryWeeks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/orders/delivery-weeks`, { withCredentials: true });
      setDeliveryWeeks(data);
    } catch (e) {
      // Error handled silently
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch =
      o.organisation_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || o.status === filterStatus;
    const matchesWeek = filterWeek === 'all' || o.delivery_week === filterWeek;
    return matchesSearch && matchesStatus && matchesWeek;
  });

  const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every(o => selectedIds.has(o.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      await axios.put(`${API_URL}/api/admin/orders/bulk-status`, {
        order_ids: Array.from(selectedIds),
        status: bulkStatus
      }, { withCredentials: true });
      toast.success(`${selectedIds.size} order(s) updated to ${bulkStatus}`);
      setSelectedIds(new Set());
      setBulkStatus('');
      fetchOrders();
    } catch (e) {
      toast.error('Failed to update orders');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/orders/${orderId}`, { status: newStatus }, { withCredentials: true });
      toast.success('Order status updated');
      fetchOrders();
    } catch (e) {
      toast.error('Failed to update order');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/orders/${orderId}`, { withCredentials: true });
      toast.success('Order deleted successfully');
      selectedIds.delete(orderId);
      setSelectedIds(new Set(selectedIds));
      fetchOrders();
      fetchDeliveryWeeks();
    } catch (e) {
      toast.error('Failed to delete order');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = filterWeek !== 'all' ? { delivery_week: filterWeek } : {};
      const response = await axios.get(`${API_URL}/api/admin/orders/export`, {
        withCredentials: true, responseType: 'blob', params
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const weekLabel = filterWeek !== 'all' ? filterWeek : format(new Date(), 'yyyyMMdd_HHmmss');
      link.setAttribute('download', `munchd_orders_${weekLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Orders exported successfully');
    } catch (e) {
      toast.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const formatWeekLabel = (dateStr) => {
    try { return `Week of ${format(new Date(dateStr), 'dd MMM yyyy')}`; }
    catch { return dateStr; }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Orders</h1>
            <p className="text-sm text-slate-500 mt-1">
              {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}
              {filterWeek !== 'all' && ` for ${formatWeekLabel(filterWeek)}`}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button onClick={() => { setEditingOrder(null); setEditorOpen(true); }}
              className="rounded-full bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none" data-testid="create-order-button">
              <Plus className="w-4 h-4 mr-2" />Create Order
            </Button>
            <Button onClick={handleExport} disabled={exporting || orders.length === 0}
              className="rounded-full bg-green-600 hover:bg-green-700 flex-1 sm:flex-none" data-testid="export-orders-button">
              {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
                : <><Download className="w-4 h-4 mr-2" /><span className="sm:inline">{filterWeek !== 'all' ? 'Export Week' : 'Export All'}</span></>}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search organisation or email..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-xl" data-testid="search-orders-input" />
          </div>
          <div className="flex gap-2">
            <Select value={filterWeek} onValueChange={v => { setFilterWeek(v); setSelectedIds(new Set()); }}>
              <SelectTrigger className="flex-1 sm:w-[200px] rounded-xl" data-testid="filter-week-select">
                <Calendar className="w-4 h-4 mr-1 text-slate-400 flex-shrink-0" /><SelectValue placeholder="All Weeks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Delivery Weeks</SelectItem>
                {deliveryWeeks.map(dw => <SelectItem key={dw} value={dw}>{formatWeekLabel(dw)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="flex-1 sm:w-[150px] rounded-xl" data-testid="filter-status-select">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <Card className="border-green-200 bg-green-50 shadow-sm">
            <CardContent className="p-3 flex flex-col sm:flex-row items-center gap-3">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-green-700" />
                <span className="text-sm font-medium text-green-800">{selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className="w-[160px] h-9 rounded-lg bg-white" data-testid="bulk-status-select">
                    <SelectValue placeholder="Set status to..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleBulkStatusChange} disabled={!bulkStatus || bulkUpdating}
                  className="rounded-full bg-green-600 hover:bg-green-700 h-9" data-testid="bulk-update-button">
                  {bulkUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
                <Button variant="ghost" onClick={() => setSelectedIds(new Set())}
                  className="rounded-full h-9 text-slate-600" data-testid="clear-selection-button">Clear</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Orders Table */}
        <Card className="border-slate-200/60 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox checked={allFilteredSelected && filteredOrders.length > 0}
                        onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Delivery Week</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />No orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`}
                        className={selectedIds.has(order.id) ? 'bg-green-50/50' : ''}>
                        <TableCell>
                          <Checkbox checked={selectedIds.has(order.id)}
                            onCheckedChange={() => toggleSelect(order.id)}
                            data-testid={`select-order-${order.id}`} />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{order.organisation_name || 'N/A'}</p>
                            <p className="text-sm text-slate-500">{order.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{order.delivery_week ? format(new Date(order.delivery_week), 'dd MMM yyyy') : 'N/A'}</TableCell>
                        <TableCell>{order.items?.length || 0} items</TableCell>
                        <TableCell>{order.catering_plan}</TableCell>
                        <TableCell>
                          <Select value={order.status} onValueChange={(value) => handleStatusChange(order.id, value)}>
                            <SelectTrigger className="w-[120px] h-8 rounded-lg" data-testid={`order-status-${order.id}`}>
                              <Badge className={`${getStatusBadge(order.status)} capitalize`}>{order.status}</Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>{order.created_at ? format(new Date(order.created_at), 'dd MMM yyyy') : 'N/A'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setViewingOrder(order)}
                              className="h-8 w-8" data-testid={`view-order-${order.id}`}><Eye className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => { setEditingOrder(order); setEditorOpen(true); }}
                              className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-order-${order.id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(order.id)}
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-order-${order.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* View Order Dialog */}
        <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Order Details</DialogTitle></DialogHeader>
            {viewingOrder && (
              <div className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  {[
                    ['Organisation', viewingOrder.organisation_name],
                    ['Email', viewingOrder.user_email],
                    ['Delivery Week', viewingOrder.delivery_week ? format(new Date(viewingOrder.delivery_week), 'PPP') : 'N/A'],
                    ['Catering Plan', viewingOrder.catering_plan],
                    viewingOrder.addon_preference && ['Add-on Preference', viewingOrder.addon_preference],
                    viewingOrder.hte_meal_type && ['HTE Meal Type', viewingOrder.hte_meal_type],
                    ['Delivery Option', viewingOrder.delivery_option || 'Standard (Pre-12pm)'],
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-600">{label}:</span>
                      <span className="font-medium">{val || 'N/A'}</span>
                    </div>
                  ))}
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status:</span>
                    <Badge className={`${getStatusBadge(viewingOrder.status)} capitalize`}>{viewingOrder.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Created:</span>
                    <span className="font-medium">{viewingOrder.created_at ? format(new Date(viewingOrder.created_at), 'PPP, HH:mm') : 'N/A'}</span>
                  </div>
                </div>
                {['Monday', 'Wednesday'].map(day => {
                  const dayItems = viewingOrder.items?.filter(i => i.delivery_day === day) || [];
                  if (dayItems.length === 0) return null;
                  return (
                    <div key={day} className="space-y-2">
                      <h4 className="font-medium text-slate-900">{day} Delivery ({dayItems.length} items)</h4>
                      <div className="border border-slate-200 rounded-xl divide-y divide-slate-200">
                        {dayItems.map((item) => (
                          <div key={`${item.product_id}-${item.product_name}`} className="p-3 flex justify-between items-center">
                            <div><p className="font-medium text-slate-900">{item.product_name}</p><p className="text-sm text-slate-500">{item.category_name}</p></div>
                            <Badge variant="secondary">x{item.quantity}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {viewingOrder.addon_selections?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Add-ons</h4>
                    <div className="flex flex-wrap gap-2">
                      {viewingOrder.addon_selections.map((addon) => (
                        <Badge key={`${addon.addon_name}-${addon.addon_type}`} className={addon.addon_type === 'premium' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                          {addon.addon_name} x{addon.quantity}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {viewingOrder.notes && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-900">Allergy & Dietary Notes</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3">{viewingOrder.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewingOrder(null)} className="rounded-full">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Order Editor (Edit/Create) */}
        <AdminOrderEditor
          open={editorOpen}
          onClose={() => { setEditorOpen(false); setEditingOrder(null); }}
          order={editingOrder}
          clients={clients}
          onSaved={() => { fetchOrders(); fetchDeliveryWeeks(); }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
